import { useEffect, useState } from "react";
import client, { fmt } from "@/lib/api";
import { Panel } from "@/components/Layout";
import { Link } from "react-router-dom";
import {
    Sparkles, Radar, Wallet, Network, TrendingUp, Activity,
    Search, Mail, Megaphone, Tv, Globe, ArrowUpRight, Flame, Crown, Shield,
} from "lucide-react";

const TELEMETRY_BG =
    "https://static.prod-images.emergentagent.com/jobs/8041819d-7954-4bde-9fe6-e6847159f659/images/4f93b1380496831cae606c2f05a9730605ac14178089ee5131ed75e3d01d81d6.png";

const CHANNEL_ICONS = {
    search: Search, social: Megaphone, display: Tv, email: Mail, influencer: Sparkles, seo: Globe,
};

export default function CommandCenter() {
    const [metrics, setMetrics] = useState(null);
    const [leads, setLeads] = useState([]);
    const [referrals, setReferrals] = useState([]);
    const [wallet, setWallet] = useState(null);
    const [signals, setSignals] = useState([]);

    useEffect(() => {
        const load = async () => {
            try {
                const [m, l, r, w, s] = await Promise.all([
                    client.get("/metrics/overview"),
                    client.get("/leads"),
                    client.get("/referrals"),
                    client.get("/wallet/totals"),
                    client.get("/signals"),
                ]);
                setMetrics(m.data); setLeads(l.data); setReferrals(r.data);
                setWallet(w.data); setSignals(s.data);
            } catch (e) { console.error(e); }
        };
        load();
    }, []);

    if (!metrics) {
        return <div className="text-[#888] label-tag" data-testid="dashboard-loading">Booting telemetry...</div>;
    }

    return (
        <div className="space-y-6" data-testid="command-center">
            {/* Hero */}
            <section className="hud-panel p-6 lg:p-8 relative overflow-hidden fade-up" data-testid="dashboard-hero">
                <div className="hud-corner-tl" /><div className="hud-corner-tr" />
                <div className="hud-corner-bl" /><div className="hud-corner-br" />
                <div className="absolute inset-0 opacity-25">
                    <img src={TELEMETRY_BG} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
                <div className="relative z-10 grid lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
                    <div className="max-w-3xl">
                        <div className="label-tag mb-3">/// LINEAGE DEPLOYMENT — SOVEREIGN HULL</div>
                        <h1 className="heading-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-4">
                            Greetings, <span className="text-gold">Admiral Akanksha</span>.<br />
                            The marketing hull is <span className="text-gold">operational</span>.
                        </h1>
                        <p className="text-[#aaa] text-sm sm:text-base max-w-2xl">
                            {metrics.active_campaigns} active campaigns running. {metrics.hot_leads} hot leads in queue.
                            {' '}{metrics.incoming_referrals} cross-fleet referrals incoming.
                            Heir vault accumulating for <span className="text-gold">Ashairah</span>.
                        </p>
                    </div>
                    <div className="hud-panel p-5" data-testid="lineage-card">
                        <div className="hud-corner-tl" /><div className="hud-corner-tr" />
                        <div className="hud-corner-bl" /><div className="hud-corner-br" />
                        <div className="label-tag mb-3 relative z-10">Active Lineage</div>
                        <div className="grid grid-cols-2 gap-3 relative z-10">
                            <div className="border border-gold-edge p-3">
                                <div className="label-tag">Admiral</div>
                                <div className="heading-display text-xl mt-1">Akanksha</div>
                                <div className="text-[10px] mono text-white/50 mt-1 uppercase">60% Sovereign</div>
                            </div>
                            <div className="border border-gold-edge p-3">
                                <div className="label-tag">Heir</div>
                                <div className="heading-display text-xl mt-1">Ashairah</div>
                                <div className="text-[10px] mono text-white/50 mt-1 uppercase">5% + 2% Tax</div>
                            </div>
                        </div>
                        <div className="circuit-divider my-4"></div>
                        <div className="flex items-center justify-between text-[10px] tracking-[0.2em] uppercase relative z-10">
                            <span className="text-white/60">Heir Vault YTD</span>
                            <span className="text-gold font-bold mono">{fmt.money(metrics.heir_vault_total || 0)}</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* KPIs */}
            <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 fade-up stagger-1" data-testid="kpi-row">
                <Kpi label="Total Spend" value={fmt.money(metrics.total_spend)} sub={`${metrics.active_campaigns} active`} icon={TrendingUp} testId="kpi-spend" />
                <Kpi label="Attributed Revenue" value={fmt.money(metrics.total_revenue)} sub={`${metrics.conversions.toLocaleString()} conversions`} icon={Sparkles} testId="kpi-revenue" highlight />
                <Kpi label="ROAS" value={`${metrics.roas.toFixed(2)}×`} sub={`${metrics.ctr.toFixed(2)}% CTR`} icon={Activity} testId="kpi-roas" />
                <Kpi label="Wallet Processed" value={fmt.money(wallet?.gross_total || 0)} sub="60/30/10 distributed" icon={Wallet} testId="kpi-wallet" />
            </section>

            {/* Module Grid */}
            <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Channel telemetry */}
                <Panel
                    tag="// MODULE 01 — CHANNEL TELEMETRY"
                    title="Performance by Channel"
                    testId="channel-mix"
                    className="lg:col-span-2 fade-up stagger-2"
                    action={
                        <Link to="/hud" className="btn-ghost-gold inline-flex items-center gap-1 !py-2 !px-3 !text-[10px]" data-testid="open-hud">
                            View HUD <ArrowUpRight className="w-3 h-3" />
                        </Link>
                    }
                >
                    <div className="space-y-3">
                        {Object.entries(metrics.by_channel || {}).map(([ch, data]) => {
                            const roas = data.spend > 0 ? data.revenue / data.spend : 0;
                            const pct = Math.min(100, (roas / 6) * 100);
                            const Icon = CHANNEL_ICONS[ch] || Globe;
                            return (
                                <div key={ch} data-testid={`channel-row-${ch}`}>
                                    <div className="flex justify-between items-center text-xs mb-1">
                                        <span className="inline-flex items-center gap-2 uppercase tracking-[0.2em] mono text-white/80">
                                            <Icon className="w-3 h-3 text-gold" /> {ch}
                                        </span>
                                        <span className="mono text-gold font-bold">{roas.toFixed(2)}× ROAS</span>
                                    </div>
                                    <div className="risk-bar"><div className="risk-bar-fill" style={{ width: `${pct}%` }} /></div>
                                    <div className="flex justify-between text-[10px] mono mt-1 text-white/40 uppercase">
                                        <span>{fmt.money(data.spend)} spend</span>
                                        <span>{fmt.money(data.revenue)} revenue</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Panel>

                {/* Brand Signals */}
                <Panel
                    tag="// MODULE 02 — BRAND RESONANCE"
                    title="Signal Index"
                    testId="brand-sentiment"
                    className="fade-up stagger-3"
                    action={
                        <Link to="/signals" className="btn-ghost-gold inline-flex items-center gap-1 !py-2 !px-3 !text-[10px]" data-testid="open-signals">
                            <Sparkles className="w-3 h-3" />
                        </Link>
                    }
                >
                    <div className="grid grid-cols-3 gap-2 mb-4">
                        <SentimentCell label="Positive" value={metrics.brand_sentiment?.positive ?? 0} color="text-[#34C759]" />
                        <SentimentCell label="Neutral" value={(metrics.brand_sentiment?.total ?? 0) - (metrics.brand_sentiment?.positive ?? 0) - (metrics.brand_sentiment?.negative ?? 0)} color="text-white/70" />
                        <SentimentCell label="Negative" value={metrics.brand_sentiment?.negative ?? 0} color="text-[#FF3B30]" />
                    </div>
                    <div className="space-y-2 max-h-[180px] overflow-y-auto pr-1">
                        {signals.slice(0, 4).map((s) => (
                            <div key={s.id} className="border-l border-gold-edge pl-3 py-1" data-testid={`mini-signal-${s.id}`}>
                                <div className="label-tag">{s.surface}</div>
                                <div className="text-xs text-white/85 leading-snug mt-0.5">{s.headline}</div>
                            </div>
                        ))}
                    </div>
                </Panel>

                {/* Lead Sentry */}
                <Panel
                    tag="// MODULE 03 — LEAD SENTRY GOLD"
                    title={`${metrics.hot_leads} HOT signals`}
                    testId="card-sentry"
                    className="fade-up stagger-4"
                    action={
                        <Link to="/sentry" className="btn-ghost-gold inline-flex items-center gap-1 !py-2 !px-3 !text-[10px]" data-testid="open-sentry">
                            <Radar className="w-3 h-3" />
                        </Link>
                    }
                >
                    <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
                        {leads.slice(0, 5).map((l) => (
                            <div key={l.id} className="flex items-start justify-between gap-2 border-b border-[#1a1a1a] pb-2" data-testid={`mini-lead-${l.id}`}>
                                <div className="min-w-0">
                                    <div className="text-sm font-bold truncate">{l.full_name}</div>
                                    <div className="label-tag text-[#888] truncate">{l.interest}</div>
                                </div>
                                <div className="text-right shrink-0">
                                    <div className={`mono text-sm font-bold ${l.score >= 80 ? "text-gold" : "text-white"}`}>{l.score}</div>
                                    <div className="label-tag text-[#666]">{fmt.money(l.estimated_value)}</div>
                                </div>
                            </div>
                        ))}
                        {leads.length === 0 && <div className="text-[#666] text-sm">No signals detected yet.</div>}
                    </div>
                </Panel>

                {/* Wallet */}
                <Panel
                    tag="// MODULE 04 — 60/30/10 WALLET"
                    title="Sovereign Revenue Engine"
                    testId="card-wallet"
                    className="fade-up stagger-5"
                    action={
                        <Link to="/wallet" className="btn-ghost-gold inline-flex items-center gap-1 !py-2 !px-3 !text-[10px]" data-testid="open-wallet">
                            <Wallet className="w-3 h-3" />
                        </Link>
                    }
                >
                    <SplitRow label="Admiral Akanksha" pct={60} amount={wallet?.admiral_total || 0} accent />
                    <SplitRow label="Fleet" pct={30} amount={wallet?.fleet_total || 0} />
                    <SplitRow label="CyberKids" pct={10} amount={wallet?.cyberkids_total || 0} />
                    <div className="mt-3 pt-3 border-t border-gold/30">
                        <div className="flex items-center justify-between">
                            <span className="label-tag inline-flex items-center gap-1"><Shield className="w-3 h-3" /> Heir · Ashairah</span>
                            <span className="metric-value text-base text-gold">{fmt.money(wallet?.heir_vault_total || 0)}</span>
                        </div>
                        <div className="text-[10px] mono text-white/40 mt-1">Ancestral 5% + Live 2%</div>
                    </div>
                </Panel>

                {/* Cross-Fleet */}
                <Panel
                    tag="// MODULE 05 — CROSS-FLEET BRIDGE"
                    title={`${metrics.incoming_referrals} incoming signals`}
                    testId="card-bridge"
                    className="lg:col-span-2 fade-up stagger-5"
                    action={
                        <Link to="/bridge" className="btn-ghost-gold inline-flex items-center gap-1 !py-2 !px-3 !text-[10px]" data-testid="open-bridge">
                            <Network className="w-3 h-3" />
                        </Link>
                    }
                >
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                        {["real_estate", "logistics", "solar", "protection"].map((f) => {
                            const count = referrals.filter((r) => r.fleet === f).length;
                            return (
                                <div key={f} className="border border-gold-edge p-3 flex items-center justify-between" data-testid={`fleet-stat-${f}`}>
                                    <div>
                                        <div className="label-tag">{f.replace("_", " ")}</div>
                                        <div className="metric-value text-2xl text-white">{count}</div>
                                    </div>
                                    <Activity className="w-5 h-5 text-gold" />
                                </div>
                            );
                        })}
                    </div>
                    <div className="space-y-2 max-h-[160px] overflow-y-auto pr-1">
                        {referrals.slice(0, 4).map((r) => (
                            <div key={r.id} className="flex items-center justify-between gap-3 border-b border-[#1a1a1a] pb-2" data-testid={`mini-ref-${r.id}`}>
                                <div className="min-w-0">
                                    <div className="text-sm font-bold truncate">{r.client_name}</div>
                                    <div className="label-tag text-[#888]">{r.referrer_name}</div>
                                </div>
                                <span className="label-tag text-gold uppercase">{r.interest}</span>
                            </div>
                        ))}
                    </div>
                </Panel>
            </section>
        </div>
    );
}

function Kpi({ label, value, sub, icon: Icon, testId, highlight }) {
    return (
        <div className={`hud-panel p-5 ${highlight ? "border-gold" : ""}`} data-testid={testId}>
            <div className="hud-corner-tl" /><div className="hud-corner-tr" />
            <div className="hud-corner-bl" /><div className="hud-corner-br" />
            <div className="flex items-start justify-between mb-2 relative z-10">
                <div className="label-tag">{label}</div>
                <Icon className="w-4 h-4 text-gold" />
            </div>
            <div className={`metric-value text-3xl relative z-10 ${highlight ? "text-gold" : "text-white"}`}>{value}</div>
            <div className="label-tag text-[#666] mt-1 relative z-10">{sub}</div>
        </div>
    );
}

function SentimentCell({ label, value, color }) {
    return (
        <div className="border border-gold-edge p-3 text-center">
            <div className={`text-2xl font-black ${color}`}>{value}</div>
            <div className="label-tag mt-1">{label}</div>
        </div>
    );
}

function SplitRow({ label, pct, amount, accent }) {
    return (
        <div className="mb-3" data-testid={`split-${label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center justify-between mb-1">
                <div className={`text-sm font-bold uppercase tracking-wider ${accent ? "text-gold" : "text-white"}`}>
                    {label} <span className="text-[#666] font-normal">— {pct}%</span>
                </div>
                <div className="mono text-sm font-bold">{fmt.money(amount)}</div>
            </div>
            <div className="risk-bar"><div className="risk-bar-fill" style={{ width: `${pct}%`, background: accent ? "#D4AF37" : "#332A0C" }} /></div>
        </div>
    );
}
